/**
 * MapLibre + Protomaps/PMTiles pickup map (Phase B). Renders only when WebGL is
 * available AND a PMTiles basemap URL is configured (VITE_PMTILES_URL). Until
 * the Serbia/Balkans .pmtiles extract is hosted on R2, mapAvailable() is false
 * and callers show a label-only / directions fallback instead — so this never
 * breaks the live app before tiles exist.
 *
 * - editable: tap to place the pin (coords rounded to 3dp by the caller).
 * - read-only: shows the driver's pin.
 * Map style follows the Telegram colour scheme (light/dark).
 */
import { useEffect, useRef } from 'react';

const PMTILES_URL = import.meta.env.VITE_PMTILES_URL as string | undefined;
// Glyphs for vector labels (free static CDN from Protomaps assets).
const GLYPHS = 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf';

export function mapAvailable(): boolean {
  if (!PMTILES_URL) return false;
  try {
    const c = document.createElement('canvas');
    return Boolean(
      window.WebGLRenderingContext &&
        (c.getContext('webgl') || c.getContext('experimental-webgl')),
    );
  } catch {
    return false;
  }
}

interface Props {
  lat: number | null;
  lng: number | null;
  /** Map centre when no pin is set yet (e.g. the chosen origin city). */
  centerLat: number;
  centerLng: number;
  editable?: boolean;
  onPick?: (lat: number, lng: number) => void;
  dark?: boolean;
  className?: string;
}

export function PickupMap({
  lat,
  lng,
  centerLat,
  centerLng,
  editable,
  onPick,
  dark,
  className,
}: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!PMTILES_URL || !ref.current) return;
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let map: any;
    let resizeCleanup: (() => void) | null = null;
    void (async () => {
      const [maplibre, pmtiles, themes] = await Promise.all([
        import('maplibre-gl'),
        import('pmtiles'),
        import('protomaps-themes-base'),
        import('maplibre-gl/dist/maplibre-gl.css'),
      ]);
      if (cancelled || !ref.current) return;
      const maplibregl = maplibre.default;
      const protocol = new pmtiles.Protocol();
      maplibregl.addProtocol('pmtiles', protocol.tile);
      const flavor = dark ? 'dark' : 'light';
      map = new maplibregl.Map({
        container: ref.current,
        center: [lng ?? centerLng, lat ?? centerLat],
        zoom: 13,
        attributionControl: false,
        dragRotate: false,
        // Avoid trapping the page's vertical scroll inside the sheet/step.
        cooperativeGestures: true,
        style: {
          version: 8,
          glyphs: GLYPHS,
          sources: {
            protomaps: {
              type: 'vector',
              url: `pmtiles://${PMTILES_URL}`,
              attribution: '© OpenStreetMap',
            },
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          layers: (themes as any).default('protomaps', flavor),
        },
      });
      // Surface tile/style failures instead of a silent blank map.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.on('error', (e: any) => console.warn('[waylo map]', e?.error?.message ?? e));
      // The map often initialises inside an animated/just-shown container, so its
      // canvas can size to 0 before layout settles → a blank map even though tiles
      // load. Force a resize on load, after the animation, and on container resize.
      map.on('load', () => map.resize());
      const t1 = setTimeout(() => !cancelled && map && map.resize(), 250);
      const ro = new ResizeObserver(() => map && map.resize());
      ro.observe(ref.current);
      resizeCleanup = () => {
        clearTimeout(t1);
        ro.disconnect();
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let marker: any = null;
      const place = (la: number, ln: number): void => {
        if (!marker) marker = new maplibregl.Marker({ color: '#67B36B' }).setLngLat([ln, la]).addTo(map);
        else marker.setLngLat([ln, la]);
      };
      if (lat != null && lng != null) place(lat, lng);
      if (editable) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map.on('click', (e: any) => {
          const la = Math.round(e.lngLat.lat * 1000) / 1000;
          const ln = Math.round(e.lngLat.lng * 1000) / 1000;
          place(la, ln);
          onPick?.(la, ln);
        });
      }
    })();
    return () => {
      cancelled = true;
      if (resizeCleanup) resizeCleanup();
      if (map) map.remove();
    };
    // Init once; lat/lng updates while editing are handled via the marker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} className={className ?? 'h-56 w-full overflow-hidden rounded-card'} />;
}
